import { useMemo, useState } from 'react';
import { Switch, Tooltip } from 'antd';

import {
  garmentJobsKey,
  useGarmentJobs,
  type GarmentJob,
  type GarmentJobBatch,
} from '@/api/garmentJobs';
import { bookingsKey } from '@/api/bookings';
import { availabilityKey, itemUnitsKey } from '@/api/masters';
import type { GarmentJobKind } from '@/api/generated/enums';
import { useApiMutation, submitVia } from '@/api/mutations';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { formatDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import type { Session } from '@core/session/types';
import { CloseJobDrawer, type CloseAction } from './jobs/CloseJobDrawer';
import { DispatchDrawer } from './jobs/DispatchDrawer';
import {
  closeGarmentJob,
  dispatchGarmentJobs,
  markReadyOnTheBooking,
  receiveGarmentJobs,
} from '@core/api/garmentJobCommands';
import { closeWords, type CloseRequest } from '@core/jobs/close';
import { withReference } from '@core/units/referenceNumber';
import {
  actionsFor,
  backProblems,
  counts,
  dispatchProblems,
  jobStage,
  kindLabel,
  readyHint,
  receiveProblems,
  withWhom,
} from '@core/jobs/queue';

/**
 * Sections 10 and 11 — what is off the rail and when it is back.
 *
 * <p>
 * <strong>One component behind two menu keys.</strong> `/cleaning` and `/alterations` are separate
 * entries with separate permissions and separate plan tiers, but they are the same screen over the
 * same table: a queue, a dispatch, a receipt. Two components would be two places for the wording
 * about what a dispatch does to the calendar to drift apart, which is the same argument
 * `TblGarmentJob` itself rests on.
 * </p>
 *
 * <p>
 * This page owns the queries and the mutations and nothing else; the two drawers are presentational
 * and take a job plus an intent, exactly as the booking counters do.
 * </p>
 */
export function GarmentJobsPage({ session, kind }: { session: Session; kind: GarmentJobKind }) {
  const [includeClosed, setIncludeClosed] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [dispatching, setDispatching] = useState(false);
  const [closing, setClosing] = useState<{ job: GarmentJob; action: CloseAction } | null>(null);

  const jobs = useGarmentJobs(kind, includeClosed);
  const labels = kindLabel(kind);
  const permission = kind === 'Cleaning' ? 'CleaningJob:Edit' : 'AlterationJob:Edit';

  const rows = useMemo(() => jobs.data ?? [], [jobs.data]);
  const tally = useMemo(() => counts(rows), [rows]);
  const chosen = useMemo(() => rows.filter((row) => selected.includes(row.id)), [rows, selected]);

  // Both drawers and the receive change what the availability calendar holds — a dispatch stretches
  // a block, finishing releases one — so a stale search would offer a garment that is at the vendor.
  const invalidates = [garmentJobsKey, availabilityKey];

  const dispatch = useApiMutation<
    GarmentJobBatch,
    { vendorId: number; expectedBackOn: string | null; notes: string | null }
  >({
    mutationFn: (body) => dispatchGarmentJobs({ jobIds: chosen.map((job) => job.id), ...body }),
    success: (batch) => `Challan ${batch.documentNumber} issued.`,
    invalidates,
    onSuccess: () => {
      setDispatching(false);
      setSelected([]);
    },
  });

  // `finish` is "back, and good": each goes back on the rail in the same press. Without it they
  // wait in "back", not yet looked at.
  const receive = useApiMutation<GarmentJob[], boolean>({
    mutationFn: (finish) =>
      receiveGarmentJobs(
        chosen.map((job) => job.id),
        finish,
      ),
    success: (back, finish) =>
      finish
        ? `${back.length} back on the rail.`
        : `${back.length} taken back in — finish each once it has been checked.`,
    invalidates,
    onSuccess: () => setSelected([]),
  });

  // The booking's own "back from the tailor" move, sent from here: it releases the tailor's block,
  // closes the job and stops the line reading "at the tailor", all together.
  const ready = useApiMutation<unknown, GarmentJob>({
    mutationFn: (job) => markReadyOnTheBooking(job),
    success: (_, job) => `${job.garmentName} is ready on booking ${job.bookingNumber ?? ''}.`,
    invalidates: [...invalidates, bookingsKey, itemUnitsKey],
  });

  const close = useApiMutation<GarmentJob, CloseRequest>({
    mutationFn: (body) => {
      if (!closing) {
        throw new Error('No job is open.');
      }

      return closeGarmentJob(closing.job.id, closing.action, body);
    },
    success: 'Job updated.',
    invalidates,
    onSuccess: () => setClosing(null),
  });

  const columns: DataViewColumn<GarmentJob>[] = [
    {
      title: 'Garment',
      dataIndex: 'garmentName',
      primary: true,
      render: (_, row) => (
        <div>
          {/*
            The job's own number leads, because it is what the shop writes on the card that goes
            with the cloth and what somebody rings up quoting. Absent on any job raised before the
            series existed — rendered as nothing rather than as an empty gap, which is what the
            index's `DocumentNumber <> ''` filter is about at the other end.
          */}
          {row.documentNumber && (
            <div className="font-mono-nums text-[13px] font-semibold text-gray-900">
              {row.documentNumber}
            </div>
          )}
          <div className="text-sm font-medium text-gray-900">{row.garmentName}</div>
          <div className="text-default font-mono-nums text-[13px]">
            {withReference(row.serialCode, row.referenceNumber)}
            {/* The challan is a different piece of paper — the vendor's, covering the whole
                dispatch — so it stays beside the serial rather than replacing the job's number. */}
            {row.challanNumber ? ` · ${row.challanNumber}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'With',
      dataIndex: 'vendorName',
      render: (_, row) => (
        <span className="text-[13px] text-gray-900">
          {/* Null is a real answer, not missing data: a shop with its own press does this itself. */}
          {row.vendorName ?? <span className="text-default">{withWhom(row)}</span>}
        </span>
      ),
    },
    {
      title: 'Stage',
      dataIndex: 'status',
      render: (_, row) => {
        const stage = jobStage(row);

        return <StatusBadge tone={stage.tone}>{stage.label}</StatusBadge>;
      },
    },
    {
      title: 'Due back',
      dataIndex: 'dueOn',
      render: (_, row) => (
        <span
          className={`font-mono-nums text-[13px] ${row.isOverdue ? 'font-semibold text-danger' : ''}`}
        >
          {row.dueOn ? formatDate(row.dueOn) : '—'}
          {row.isOverdue && ' · late'}
        </span>
      ),
    },
    {
      title: 'Out',
      dataIndex: 'daysOut',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {row.daysOut === null ? '—' : `${row.daysOut}d`}
        </span>
      ),
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {row.cost === null ? '—' : formatAmount(row.cost)}
        </span>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      trailing: true,
      render: (_, row) => {
        const can = actionsFor(row);

        return (
          <WriteAction session={session} required={permission}>
            {(disabled) => (
              <div className="flex flex-wrap items-center gap-1">
                {/* Where "Finish" would be on a booking-owned alteration: the booking's own move,
                    gated on the booking's permission, since that is the endpoint it calls. */}
                {can.ready && (
                  <WriteAction session={session} required="RentalOrder:Edit">
                    {(cannotEditBooking) => (
                      <Tooltip title={readyHint(row)} placement="left">
                        <span className="inline-flex">
                          <Button
                            icon="icon icon-badge-check"
                            disabled={cannotEditBooking || ready.isPending}
                            onClick={() => void submitVia(ready, row)}
                          >
                            Back — ready
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </WriteAction>
                )}
                {can.complete && (
                  <Button
                    icon="icon icon-badge-check"
                    disabled={disabled}
                    onClick={() => setClosing({ job: row, action: 'complete' })}
                  >
                    {closeWords(row, 'complete').label}
                  </Button>
                )}
                {can.reClean && (
                  <Button
                    icon="icon icon-rotate-ccw"
                    disabled={disabled}
                    onClick={() => setClosing({ job: row, action: 'reClean' })}
                  >
                    Again
                  </Button>
                )}
                {can.extend && (
                  <Button
                    icon="icon icon-calendar-clock"
                    disabled={disabled}
                    onClick={() => setClosing({ job: row, action: 'extend' })}
                  >
                    Date
                  </Button>
                )}
                {can.damaged && (
                  <Button
                    variant="danger"
                    icon="icon icon-triangle-alert"
                    disabled={disabled}
                    onClick={() => setClosing({ job: row, action: 'damaged' })}
                  >
                    Damaged
                  </Button>
                )}
              </div>
            )}
          </WriteAction>
        );
      },
    },
  ];

  const canReceive = chosen.length > 0 && receiveProblems(chosen).length === 0;
  const canFinish = chosen.length > 0 && backProblems(chosen).length === 0;
  // Out, but one of them is a booking's alteration: said rather than left as a greyed button, since
  // the clerk has to go to that row's "Back — ready" instead.
  const finishRefused = canReceive && !canFinish ? backProblems(chosen)[0] : undefined;
  // Never offer a write the server will refuse: Send out is for garments waiting to go, not out
  // already, and not with our own people. When the selection is not a coming-back one, say why.
  const canSend = chosen.length > 0 && dispatchProblems(chosen).length === 0;
  const sendRefused =
    chosen.length > 0 && !canSend && !canReceive ? dispatchProblems(chosen)[0] : undefined;
  const refused = finishRefused ?? sendRefused;
  const count = (can: boolean) => (chosen.length > 0 && can ? ` (${chosen.length})` : '');

  return (
    <>
      <PageHeading
        title={labels.title}
        subtitle={`What is off the rail and when it is back. Until a job is finished the garment stays blocked — which is what stops the calendar promising it.`}
        actions={
          <WriteAction session={session} required={permission}>
            {(disabled) => (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  icon="icon icon-badge-check"
                  disabled={disabled || !canFinish || receive.isPending}
                  onClick={() => void submitVia(receive, true)}
                >
                  Back — all good{count(canFinish)}
                </Button>
                <Button
                  icon="icon icon-inbox"
                  disabled={disabled || !canReceive || receive.isPending}
                  onClick={() => void submitVia(receive, false)}
                >
                  Back, not checked{count(canReceive)}
                </Button>
                <Button
                  variant="primary"
                  icon="icon icon-truck"
                  shortcut="create"
                  disabled={disabled || !canSend}
                  onClick={() => setDispatching(true)}
                >
                  Send out{count(canSend)}
                </Button>
              </div>
            )}
          </WriteAction>
        }
      />

      {refused && (
        <div className="mb-3 lg:mb-6">
          <Notice tone="info" title={refused} />
        </div>
      )}

      {tally.overdue > 0 && (
        <div className="mb-3 lg:mb-6">
          <Notice
            tone="warning"
            title={`${tally.overdue} garment${tally.overdue === 1 ? '' : 's'} past the promised date`}
          >
            Push the date back so the calendar keeps holding them. A block that expires while the
            garment is still out is one the availability search will offer to the next customer.
          </Notice>
        </div>
      )}

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <div className="mb-4 flex flex-wrap items-center gap-3 text-[13px] text-default max-sm:mb-3">
          <span>
            <strong className="text-gray-900">{tally.waiting}</strong> waiting
          </span>
          <span>
            <strong className="text-gray-900">{tally.out}</strong> out
          </span>
          <span>
            <strong className="text-gray-900">{tally.back}</strong> back
          </span>

          <label className="inline-flex cursor-pointer items-center gap-2 sm:ms-auto">
            <Switch size="small" checked={includeClosed} onChange={setIncludeClosed} />
            Show finished
          </label>
        </div>

        <DataView<GarmentJob>
          columns={columns}
          dataSource={rows}
          loading={jobs.isLoading}
          rowKey="id"
          selectedKeys={selected}
          onSelectionChange={setSelected}
          empty={
            <EmptyState
              icon="icon icon-washing-machine"
              title={includeClosed ? 'Nothing here yet' : 'Nothing off the rail'}
              hint={
                includeClosed
                  ? `A ${labels.verb} job appears here as soon as a garment comes back.`
                  : `Every garment is on the rail. Turn on “show finished” to see what has been through.`
              }
            />
          }
        />
      </Card>

      {dispatching && (
        <DispatchDrawer
          kind={kind}
          jobs={chosen}
          branchId={session.branch?.id}
          saving={dispatch.isPending}
          onDispatch={(vendorId, expectedBackOn, notes) =>
            void submitVia(dispatch, { vendorId, expectedBackOn, notes })
          }
          onClose={() => setDispatching(false)}
        />
      )}

      {closing && (
        <CloseJobDrawer
          job={closing.job}
          action={closing.action}
          saving={close.isPending}
          onCommit={(payload) => void submitVia(close, payload)}
          onClose={() => setClosing(null)}
        />
      )}
    </>
  );
}
